import { createColumnHelper } from "@tanstack/react-table";
import { Terminal } from "../../services/accountService";
import TableHeader from "../../components/TableHeader";
import { TerminalsTableMeta } from "./Terminals";
import Switch from "../../components/Switch";
import EditControls from "./EditControls";
import Input from "../../components/Input";
import { useState } from "react";

type TerminalFriendlyNameColumnProps = {
  id: string;
  friendlyName: string;
  currentEditingTerminal: Terminal | null;
  setCurrentEditingTerminal: (terminal: Terminal) => void;
};

function TerminalFriendlyNameColumn({
  id,
  friendlyName,
  currentEditingTerminal,
  setCurrentEditingTerminal,
}: TerminalFriendlyNameColumnProps) {
  const [localFriendlyName, setLocalFriendlyName] = useState(friendlyName);

  return id === currentEditingTerminal?.id ? (
    <Input
      id={id + "-friendlyName"}
      value={localFriendlyName}
      onChange={(e) => {
        setLocalFriendlyName(e.target.value);
      }}
      onBlur={(e) => {
        if (currentEditingTerminal) {
          setCurrentEditingTerminal({
            ...currentEditingTerminal,
            friendlyName: localFriendlyName,
          });
        }
        // Did they click save or cancel, the blur blocks those events so we need to trigger it but on a delay
        // to give time for the name change to propogate.
        if (e.relatedTarget && e.relatedTarget.id) {
          var target = e.relatedTarget.id;
          setTimeout(() => {
            document.getElementById(target)?.click();
          }, 100);
        }
      }}
    />
  ) : (
    <div className="truncate">{friendlyName}</div>
  );
}

const columnHelper = createColumnHelper<Terminal>();
export const getTerminalsTableColumns = () => [
  columnHelper.accessor("friendlyName", {
    header: () => <TableHeader text="Name" />,
    cell: (info) => {
      const { id, friendlyName } = info.row.original;
      const { currentEditingTerminal, setCurrentEditingTerminal } = info.table
        .options.meta as TerminalsTableMeta;

      return (
        <TerminalFriendlyNameColumn
          id={id}
          friendlyName={
            id === currentEditingTerminal?.id
              ? currentEditingTerminal.friendlyName
              : friendlyName
          }
          currentEditingTerminal={currentEditingTerminal}
          setCurrentEditingTerminal={setCurrentEditingTerminal}
        />
      );
    },
    minSize: 200,
    size: 200,
  }),
  columnHelper.accessor("terminalId", {
    header: () => <TableHeader text="Id" />,
    cell: (info) => {
      return <div className="truncate">{info.getValue()}</div>;
    },
    minSize: 200,
    size: 200,
  }),
  columnHelper.accessor("deviceName", {
    header: () => <TableHeader text="Model" />,
    cell: (info) => {
      return <div className="truncate">{info.getValue()}</div>;
    },
    minSize: 200,
    size: 200,
  }),
  columnHelper.accessor("status", {
    header: () => <TableHeader text="Status" />,
    cell: (info) => {
      const status = info.getValue();

      return <div>{status} </div>;
    },
    minSize: 200,
    size: 200,
  }),
  columnHelper.accessor("isConnected", {
    header: () => <TableHeader text="Online status" />,
    cell: (info) => {
      const isConnected = info.getValue();
      const colorClass = isConnected ? "bg-green-500" : "bg-red-500";
      return (
        <div className="flex items-center gap-x-2">
          <div className={`h-2 w-2 rounded-full ${colorClass}`} />
          {isConnected ? "Connected" : "Disconnected"}
        </div>
      );
    },
  }),
  columnHelper.accessor("enabled", {
    header: () => <TableHeader text="Enabled" />,
    cell: (info) => {
      const { id, enabled } = info.row.original;
      const { currentEditingTerminal, setCurrentEditingTerminal } = info.table
        .options.meta as TerminalsTableMeta;

      return (
        <div className="flex items-center">
          <Switch
            checked={
              id === currentEditingTerminal?.id
                ? currentEditingTerminal.enabled
                : enabled
            }
            disabled={id !== currentEditingTerminal?.id}
            onChange={() =>
              setCurrentEditingTerminal({
                ...currentEditingTerminal!,
                enabled: !currentEditingTerminal?.enabled,
              })
            }
            label={""}
          />
        </div>
      );
    },
    minSize: 80,
    size: 80,
  }),
  columnHelper.display({
    id: "edit",
    cell: (info) => {
      const { id } = info.row.original;
      const {
        currentEditingTerminal,
        onTerminalEditStart,
        onTerminalUpdate,
        onTerminalUpdateCanceled,
        isUpdatingTerminal,
      } = info.table.options.meta as TerminalsTableMeta;
      return (
        <div className="flex items-center justify-center">
          <EditControls
            id={id}
            isEditing={id === currentEditingTerminal?.id}
            isSaving={isUpdatingTerminal}
            editButtonProps={{
              onClick: () => {
                onTerminalEditStart(info.row.original);
              },
            }}
            saveButtonProps={{
              onClick: () => {
                onTerminalUpdate();
              },
              disabled: isUpdatingTerminal,
            }}
            cancelButtonProps={{
              onClick: () => {
                onTerminalUpdateCanceled();
              },
              disabled: isUpdatingTerminal,
            }}
          />
        </div>
      );
    },
    minSize: 100,
    size: 100,
  }),
];
